import { Link } from "react-router-dom";
import PageHero from "../components/site/PageHero";
import CTABanner from "../components/site/CTABanner";
import ServiceBody from "../components/site/ServiceBody";
import CommonIssues from "../components/site/CommonIssues";
import CostFactors from "../components/site/CostFactors";
import FAQBlock from "../components/site/FAQBlock";
import ProcessSteps from "../components/site/ProcessSteps";
import useSEO from "../hooks/useSEO";
import {
  Cpu,
  Wrench,
  Search,
  AlertTriangle,
  ArrowUpCircle,
  ClipboardCheck,
  Zap,
  Settings2,
  Phone,
} from "lucide-react";

const IMG =
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_fire-cctv-access/artifacts/pnnuoixs_ChatGPT%20Image%20Aug%207%2C%202026%2C%2001_57_18%20PM.png";

export default function FireAlarms() {
  useSEO({
    title: "Fire Alarm Systems Ireland | Installation, Testing & Maintenance",
    description:
      "Fire alarm system design, installation, commissioning, testing, maintenance and repair services for commercial, industrial and public-sector premises across Ireland.",
    canonicalPath: "/services/fire-alarms",
  });

  return (
    <>
      <PageHero
        eyebrow="Fire Alarm Systems"
        title="Fire Alarm Systems"
        subtitle="A reliable fire alarm system is one of the most important safety systems within any building. Fire Safety Systems provides professional design, installation, commissioning, testing, maintenance and repair services for commercial, industrial and public-sector premises throughout Ireland."
        image={IMG}
        imageAlt="Fire alarm call-point on a commercial premises"
        ctaPrimary={{ to: "/contact", label: "Request a Quote" }}
        ctaSecondary={{ to: "/services/testing-certification", label: "Testing & Certification" }}
      />

      <ServiceBody
        includedSubtitle="Our fire alarm services"
        included={[
          { icon: Settings2, title: "System design", copy: "Alarm system design tailored to the layout and risk profile of your premises." },
          { icon: Cpu, title: "Installation", copy: "Professional installation delivered by experienced fire safety engineers." },
          { icon: Zap, title: "Commissioning", copy: "Full commissioning and hand-over so your system is ready from day one." },
          { icon: ClipboardCheck, title: "Routine testing", copy: "Scheduled testing to help keep your fire alarm operating reliably." },
          { icon: Wrench, title: "Planned preventative maintenance", copy: "Routine servicing designed to prevent faults and extend system life." },
          { icon: Search, title: "Fault finding", copy: "Diagnostic investigation of intermittent or persistent system faults." },
          { icon: AlertTriangle, title: "Emergency repairs", copy: "Responsive repair support to get your alarm system back online." },
          { icon: ArrowUpCircle, title: "System upgrades", copy: "Upgrades for legacy or partial systems needing extended coverage." },
        ]}
        whoFor={[
          "New builds requiring a full fire alarm installation",
          "Existing premises with alarm systems needing maintenance or repair",
          "Buildings requiring alarm system upgrades or extended coverage",
          "Property managers standardising alarm servicing across sites",
        ]}
        benefits={[
          "A dependable, professionally installed detection system",
          "Reduced risk of system failures through routine maintenance",
          "Clear service records supporting your fire safety programme",
          "Responsive support when repairs or fault finding are needed",
        ]}
        defaultService="Fire Alarm Systems"
      />

      <section className="bg-slate-50 border-y border-slate-200 fss-section">
        <div className="fss-container grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <div className="fss-overline">New installs &amp; existing systems</div>
            <h2 className="mt-3 font-display text-2xl md:text-3xl font-semibold text-slate-900 leading-tight">
              Support for new installations and existing systems.
            </h2>
            <p className="mt-3 text-slate-600 leading-relaxed max-w-2xl">
              We support both new fire alarm installations and existing systems
              that require maintenance, repair or upgrading. If you are unsure
              of the condition or capability of your current system, we can
              arrange a site assessment and provide a clear recommendation.
            </p>
          </div>
          <div className="lg:col-span-4 flex flex-col gap-3">
            <Link to="/contact" className="fss-btn fss-btn-primary w-full justify-center">
              Request a Quote
            </Link>
            <Link
              to="/contact"
              className="fss-btn fss-btn-outline w-full justify-center"
            >
              <Phone className="w-4 h-4 mr-2" />
              Contact FSS
            </Link>
          </div>
        </div>
      </section>

      <CommonIssues
        title="Common fire alarm concerns"
        intro="If any of the following apply, arranging an inspection is often the right first step."
        items={[
          "A fault indicator on the alarm panel",
          "Damaged detectors, call-points or sounders",
          "Frequent or unexplained false alarms",
          "Battery, standby or power issues",
          "Missing service records or unknown history",
          "Outdated equipment nearing end of life",
          "Building alterations, refits or extensions",
          "Changes in occupancy or business use",
          "A system that has not been tested recently",
        ]}
      />

      <CostFactors
        title="What affects the cost of a fire alarm system"
        intro="Fire alarm quotations vary significantly depending on your building and specification. Following a site survey we provide a clear, itemised proposal."
        factors={[
          "Building size and layout",
          "Number of floors and zones",
          "Type of building and how it is used",
          "Number and type of detection devices",
          "Existing wiring and cable infrastructure",
          "System category and specification",
          "Accessibility of installation areas",
          "Any integration or interface requirements",
          "The condition of the existing system, where applicable",
          "The overall installation timescale",
        ]}
      />

      <ProcessSteps
        eyebrow="Installation process"
        title="How a new fire alarm installation works"
        intro="A typical installation follows a structured process — we adapt the approach to the building and your requirements."
        steps={[
          { title: "Site Survey", copy: "We walk the premises to understand its use, occupancy and layout." },
          { title: "Detection Requirements", copy: "We confirm the appropriate detection and coverage for the building." },
          { title: "System Design", copy: "We design the proposed system around the survey findings." },
          { title: "Quotation", copy: "A clear, itemised proposal is prepared and shared with you." },
          { title: "Installation & Commissioning", copy: "Equipment is installed, tested and commissioned." },
          { title: "Handover & Documentation", copy: "We hand over the system with the relevant documentation." },
        ]}
        bg="slate"
      />

      <FAQBlock
        eyebrow="Fire alarm FAQ"
        title="Frequently asked fire alarm questions"
        items={[
          { q: "Can FSS work with an existing fire alarm system?", a: "Yes. We support existing systems that need testing, maintenance, repair, extension or upgrading, subject to the manufacturer, age and condition." },
          { q: "How often should a fire alarm be tested?", a: "The right schedule depends on the system, the premises and the applicable standards. We will agree an appropriate testing plan with you." },
          { q: "Can additional detection be added later?", a: "Often yes — depending on the capacity of the existing panel and whether compatible devices are still available from the manufacturer." },
          { q: "What documentation is provided?", a: "Following installation and commissioning we provide clear service and handover documentation covering the equipment installed and completed testing." },
          { q: "What information affects the quotation?", a: "Typical factors include building size and layout, number of floors, device counts, cabling and infrastructure, and any integration requirements." },
        ]}
      />

      <CTABanner
        title="Need a reliable fire alarm solution?"
        subtitle="Contact FSS to arrange a site assessment or request a quotation."
        primary={{ to: "/contact", label: "Request a Quote" }}
        secondary={{ to: "/contact", label: "Arrange a Site Survey" }}
      />
    </>
  );
}
